import { useState, useRef, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Command, CommandList, CommandItem, CommandGroup, CommandEmpty } from "@/components/ui/command";
import { Send, Loader2 } from "lucide-react";
import { useComentarios } from "@/hooks/useComentarios";
import { useOrgMembers } from "@/hooks/useOrgMembers";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { PlanGate } from "@/components/PlanGate";

function getInitials(email?: string): string {
  if (!email) return "?";
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `hace ${days}d`;
  return new Date(dateStr).toLocaleDateString("es-CO", { day: "numeric", month: "short" });
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityId: string;
  entityType: "embarque" | "documento";
  entityLabel?: string;
}

export function CommentsSheet({ open, onOpenChange, entityId, entityType, entityLabel }: Props) {
  const { user } = useAuth();
  const { comentarios, isLoading, enviarComentario } = useComentarios({ entityId, entityType });
  const { data: orgMembers = [] } = useOrgMembers();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Mention states
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(-1);

  // Auto-scroll on new comments
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comentarios.length]);

  const filteredMembers = orgMembers.filter((m) =>
    m.email.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const closeMentions = useCallback(() => {
    setShowMentions(false);
    setMentionQuery("");
    setMentionIndex(-1);
  }, []);

  const handleSelectMention = useCallback((email: string) => {
    if (mentionIndex < 0) return;
    // Replace from @ to current cursor with @email + space
    const before = draft.slice(0, mentionIndex);
    const cursorPos = textareaRef.current?.selectionStart ?? draft.length;
    const after = draft.slice(cursorPos);
    const newDraft = `${before}@${email} ${after}`;
    setDraft(newDraft);
    closeMentions();

    // Refocus and set cursor
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + email.length + 2; // @email + space
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  }, [draft, mentionIndex, closeMentions]);

  const handleSend = async () => {
    if (!draft.trim()) return;
    closeMentions();
    try {
      // Extract mentions from draft
      const mentionedEmails = Array.from(draft.matchAll(/@([\w._-]+@[\w._-]+\.\w+)/g)).map((m) => m[1]);
      const mentions = orgMembers
        .filter((m) => mentionedEmails.includes(m.email))
        .map((m) => ({ user_id: m.user_id, email: m.email }));

      await enviarComentario.mutateAsync({ content: draft, mentions: mentions.length > 0 ? mentions : null });
      setDraft("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } catch (err: any) {
      toast.error("Error al enviar comentario", { description: err.message });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMentions();
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey && !showMentions) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-expand textarea + mention detection
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDraft(value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";

    // Mention detection
    const cursorPos = el.selectionStart;
    const textUpToCursor = value.slice(0, cursorPos);

    // Find the last @ that isn't part of an email already completed
    const lastAtIndex = textUpToCursor.lastIndexOf("@");
    if (lastAtIndex >= 0) {
      // Check that @ is at start or preceded by whitespace
      const charBefore = lastAtIndex > 0 ? textUpToCursor[lastAtIndex - 1] : " ";
      if (/\s/.test(charBefore) || lastAtIndex === 0) {
        const query = textUpToCursor.slice(lastAtIndex + 1);
        // If query has no space, we're still typing the mention
        if (!/\s/.test(query)) {
          setShowMentions(true);
          setMentionQuery(query);
          setMentionIndex(lastAtIndex);
          return;
        }
      }
    }
    closeMentions();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col p-0 w-[400px] sm:max-w-[400px]">
        <SheetHeader className="px-4 py-3 border-b border-border shrink-0">
          <SheetTitle className="text-sm font-semibold">
            Comentarios {entityLabel && <span className="text-muted-foreground font-normal">— {entityLabel}</span>}
          </SheetTitle>
        </SheetHeader>

        <PlanGate allowedPlans={['PRO', 'ENTERPRISE']} featureName="Colaboración y Mensajería de Equipo">
          {/* Messages area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4 min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : comentarios.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-center">
                <p className="text-sm">No hay comentarios aún</p>
                <p className="text-xs mt-1">Sé el primero en comentar</p>
              </div>
            ) : (
              comentarios.map((c) => {
                const isOwn = c.user_id === user?.id;
                const member = orgMembers.find((m) => m.user_id === c.user_id);
                const email = member?.email || c.user_id;
                return (
                  <div key={c.id} className={`flex gap-2.5 ${isOwn ? "flex-row-reverse" : ""}`}>
                    <Avatar className="h-7 w-7 shrink-0 mt-0.5">
                      <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                        {getInitials(email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`max-w-[80%] ${isOwn ? "items-end" : "items-start"}`}>
                      <div
                        className={`rounded-xl px-3 py-2 text-sm leading-relaxed ${
                          isOwn
                            ? "bg-primary text-primary-foreground rounded-tr-sm"
                            : "bg-muted text-foreground rounded-tl-sm"
                        }`}
                      >
                        {renderContentWithMentions(c.content)}
                      </div>
                      <p className={`text-[10px] text-muted-foreground mt-1 ${isOwn ? "text-right" : ""}`}>
                        {timeAgo(c.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Input area */}
          <div className="shrink-0 border-t border-border px-4 py-3">
            <div className="relative">
              {/* Mentions dropdown */}
              {showMentions && filteredMembers.length > 0 && (
                <div className="absolute bottom-full mb-1 left-0 w-full z-50">
                  <Command className="border border-border rounded-md shadow-md bg-popover">
                    <CommandList>
                      <CommandGroup heading="Miembros del equipo">
                        {filteredMembers.slice(0, 6).map((m) => (
                          <CommandItem
                            key={m.user_id}
                            onSelect={() => handleSelectMention(m.email)}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <Avatar className="h-5 w-5">
                              <AvatarFallback className="text-[8px] font-semibold bg-primary/10 text-primary">
                                {getInitials(m.email)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm truncate">{m.email}</span>
                            <span className="text-[10px] text-muted-foreground ml-auto">{m.rol}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      {filteredMembers.length === 0 && (
                        <CommandEmpty>No se encontraron miembros</CommandEmpty>
                      )}
                    </CommandList>
                  </Command>
                </div>
              )}

              <div className="flex items-end gap-2">
                <Textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={handleInput}
                  onKeyDown={handleKeyDown}
                  placeholder="Escribe un comentario... usa @ para mencionar"
                  className="min-h-[36px] max-h-[120px] resize-none text-sm flex-1"
                  rows={1}
                />
                <Button
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={handleSend}
                  disabled={!draft.trim() || enviarComentario.isPending}
                >
                  {enviarComentario.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">Enter para enviar · Shift+Enter para salto de línea · @ para mencionar</p>
            </div>
          </div>
        </PlanGate>
      </SheetContent>
    </Sheet>
  );
}

/** Render comment text highlighting @mentions in accent color */
function renderContentWithMentions(text: string) {
  const parts = text.split(/(@[\w._-]+@[\w._-]+\.\w+)/g);
  if (parts.length === 1) return text;
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("@") ? (
          <span key={i} className="font-semibold text-accent-foreground/90">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}
